import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import { Add, DeleteOutline } from '@mui/icons-material';
import type { PSDGeneral, PSDSieveDetail } from '../../../types/psd';
import { computeSieveRowsLive } from './computations';
import { useTestFormulas } from '../../../hooks/useTestFormulas';
import { FormulaHeaderTooltip } from '../components/FormulaHeaderTooltip';

interface Props {
  general: PSDGeneral;
  rows: PSDSieveDetail[];
  onChange: (next: PSDSieveDetail[]) => void;
  readOnly?: boolean;
}

const cellSx = {
  '& .MuiInputBase-input': {
    py: 0.4, px: 0.75, fontSize: '0.78rem', textAlign: 'right' as const,
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none', margin: 0,
    },
    MozAppearance: 'textfield',
  },
};

const fmt = (v: number | null, d = 1): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : v.toFixed(d);

export const SieveDetailsGrid = ({ general, rows, onChange, readOnly = false }: Props) => {
  const theme = useTheme();
  const { formulaFor } = useTestFormulas('psd');
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRowSize, setNewRowSize] = useState('');
  const [newRowDesignation, setNewRowDesignation] = useState('');

  const sortedRows = useMemo(
    () => rows.slice().sort((a, b) => a.sortOrder - b.sortOrder || b.sieveSizeMm - a.sieveSizeMm),
    [rows],
  );

  // When hydrometer is off, sub-2mm sieves are deactivated to match legacy
  // KeyLAB Excel behavior (sub-2mm rows are only meaningful with hydrometer).
  const hydroOff = !general.hydrometerEnabled;
  const isRowInactive = (sizeMm: number) => hydroOff && sizeMm < 2;

  // Live computed columns (frontend mirror)
  const computed = useMemo(() => computeSieveRowsLive(sortedRows, general), [sortedRows, general]);

  const handleMassChange = (idx: number, raw: string) => {
    const next = sortedRows.slice();
    next[idx] = { ...next[idx], cumulativeMassRetainedG: raw === '' ? null : Number(raw) };
    onChange(next);
  };

  const handleAddRow = () => {
    if (!newRowSize.trim() || Number.isNaN(Number(newRowSize))) return;
    const sizeMm = Number(newRowSize);
    const next: PSDSieveDetail[] = [...sortedRows, {
      sieveSizeMm: sizeMm,
      sieveDesignation: newRowDesignation.trim() || `${sizeMm}mm`,
      sieveDisplayName: newRowDesignation.trim() || `${sizeMm}mm`,
      sortOrder: 999,
      isCustom: true,
      cumulativeMassRetainedG: null,
      computedSubsamplePassingPct: null,
      computedPassingWholePct: null,
    }];
    // Re-sort by size descending and reassign sortOrder
    next.sort((a, b) => b.sieveSizeMm - a.sieveSizeMm);
    next.forEach((r, i) => { r.sortOrder = i + 1; });
    onChange(next);
    setNewRowSize('');
    setNewRowDesignation('');
    setShowAddRow(false);
  };

  const handleRemoveCustomRow = (idx: number) => {
    const row = sortedRows[idx];
    if (!row.isCustom) return; // can't remove preset rows
    onChange(sortedRows.filter((_, i) => i !== idx));
  };

  const isNegative = (v: number | null): boolean => v !== null && v < 0;

  return (
    <Paper elevation={0} sx={{
      p: 1.5, borderRadius: 2,
      border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6 }}>
          Sieve Analysis
        </Typography>
        <Typography variant="caption" sx={{ ml: 1.5, color: 'text.secondary', fontSize: '0.7rem' }}>
          {rows.length} rows ({rows.filter((r) => r.isCustom).length} custom)
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Button size="small" startIcon={<Add />} onClick={() => setShowAddRow((p) => !p)}
            disabled={readOnly} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Add Row
          </Button>
        </Box>
      </Box>

      {hydroOff && (
        <Box sx={{
          p: 1, mb: 1, borderRadius: 1,
          backgroundColor: alpha(theme.palette.warning.light, 0.08),
          border: `1px dashed ${alpha(theme.palette.warning.main, 0.4)}`,
        }}>
          <Typography variant="caption" color="warning.dark" sx={{ fontSize: '0.72rem' }}>
            ⚠ Hydrometer is disabled — sub-2mm sieves are inactive (no input, no
            calculation). Enable it under <strong>Test Parameters → Hydrometer
            Configuration</strong> to activate them.
          </Typography>
        </Box>
      )}

      {showAddRow && (
        <Box sx={{
          display: 'grid', gridTemplateColumns: '120px 180px 1fr', gap: 1, mb: 1,
          p: 1, backgroundColor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1,
        }}>
          <TextField placeholder="Size (mm)" type="number" size="small"
            value={newRowSize} onChange={(e) => setNewRowSize(e.target.value)}
            sx={cellSx}
            autoFocus
          />
          <TextField placeholder="Designation (e.g. #325)" size="small"
            value={newRowDesignation} onChange={(e) => setNewRowDesignation(e.target.value)}
            sx={cellSx}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={handleAddRow} disabled={!newRowSize.trim()}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Add
            </Button>
            <Button size="small" onClick={() => setShowAddRow(false)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '40px 90px 90px 130px 130px 130px 36px',
        rowGap: 0.4, columnGap: 1, alignItems: 'center',
      }}>
        {/* Header row */}
        <Box />
        <FormulaHeaderTooltip label="Sieve" formula={formulaFor('sieveDesignation')?.formula} note={formulaFor('sieveDesignation')?.note}>
          <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>Sieve</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="Size (mm)" formula={formulaFor('sieveSizeMm')?.formula} note={formulaFor('sieveSizeMm')?.note}>
          <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Size (mm)</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="Cumulative Mass Retained (g)" formula={formulaFor('cumulativeMassRetainedG')?.formula} note={formulaFor('cumulativeMassRetainedG')?.note}>
          <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Cumulative<br/>Mass Retained (g)</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="% Subsample Passing" formula={formulaFor('subsamplePassingPct')?.formula} note={formulaFor('subsamplePassingPct')?.note}>
          <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>% Subsample<br/>Passing</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="% Passing Whole" formula={formulaFor('passingWholePct')?.formula} note={formulaFor('passingWholePct')?.note}>
          <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>% Passing<br/>Whole</Box>
        </FormulaHeaderTooltip>
        <Box />

        {sortedRows.map((row, idx) => {
          const c = computed[idx];
          const subNeg = isNegative(c.subsamplePassingPct);
          const wholeNeg = isNegative(c.passingWholePct);
          const inactive = isRowInactive(row.sieveSizeMm);
          return (
            <RowCells
              key={row.psdSieveDetailKey ?? `new-${idx}`}
              row={row}
              idx={idx}
              cellSx={cellSx}
              theme={theme}
              readOnly={readOnly}
              inactive={inactive}
              subNeg={subNeg}
              wholeNeg={wholeNeg}
              subPct={c.subsamplePassingPct}
              wholePct={c.passingWholePct}
              onMassChange={handleMassChange}
              onRemove={handleRemoveCustomRow}
              fmt={fmt}
            />
          );
        })}
      </Box>
    </Paper>
  );
};

interface RowCellsProps {
  row: PSDSieveDetail;
  idx: number;
  readOnly: boolean;
  inactive: boolean;
  subNeg: boolean;
  wholeNeg: boolean;
  subPct: number | null;
  wholePct: number | null;
  onMassChange: (idx: number, raw: string) => void;
  onRemove: (idx: number) => void;
  cellSx: object;
  theme: Theme;
  fmt: (v: number | null, d?: number) => string;
}

const RowCells = ({
  row, idx, readOnly, inactive, subNeg, wholeNeg, subPct, wholePct,
  onMassChange, onRemove, cellSx, theme, fmt,
}: RowCellsProps) => {
  const inactiveTip = inactive ? 'Hydrometer is off — sub-2mm sieves are inactive' : '';
  const dimmed = inactive ? { color: 'text.disabled' as const } : {};
  return (
  <>
    <Box sx={{ fontSize: '0.7rem', textAlign: 'center', ...dimmed, color: inactive ? 'text.disabled' : 'text.secondary' }}>
      {row.sortOrder}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ fontSize: '0.78rem', ...dimmed }}>
        {row.sieveDesignation || row.sieveDisplayName || '—'}
      </Typography>
      {row.isCustom && (
        <Typography sx={{ fontSize: '0.6rem', color: 'warning.main', fontWeight: 700 }}>•custom</Typography>
      )}
    </Box>
    <Box sx={{ fontSize: '0.78rem', textAlign: 'right', color: inactive ? 'text.disabled' : 'text.secondary' }}>
      {row.sieveSizeMm}
    </Box>
    <TextField type="number" size="small"
      value={row.cumulativeMassRetainedG ?? ''}
      onChange={(e) => onMassChange(idx, e.target.value)}
      sx={cellSx}
      disabled={readOnly || inactive}
      title={inactiveTip || undefined}
    />
    <Box sx={{
      fontSize: '0.78rem', textAlign: 'right', pr: 1,
      backgroundColor: subNeg ? alpha(theme.palette.error.main, 0.12) : 'transparent',
      borderRadius: 0.5,
      color: subNeg ? 'error.main' : (subPct === null ? 'text.disabled' : 'text.primary'),
      fontWeight: subNeg ? 700 : 500,
    }}>
      {fmt(subPct, 1)}
    </Box>
    <Box sx={{
      fontSize: '0.78rem', textAlign: 'right', pr: 1,
      backgroundColor: wholeNeg ? alpha(theme.palette.error.main, 0.12) : 'transparent',
      borderRadius: 0.5,
      color: wholeNeg ? 'error.main' : (wholePct === null ? 'text.disabled' : 'text.primary'),
      fontWeight: wholeNeg ? 700 : 500,
    }}>
      {fmt(wholePct, 1)}
    </Box>
    <Box>
      {row.isCustom && (
        <Tooltip title="Remove custom row">
          <IconButton size="small" color="error" onClick={() => onRemove(idx)} disabled={readOnly}>
            <DeleteOutline sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </>
  );
};
